import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { ToastProvider } from './contexts/ToastContext';
import { CallProvider } from './contexts/CallContext';
import CallOverlay from './components/CallOverlay';
import { GroupCallProvider } from './contexts/GroupCallContext';
import GroupCallOverlay from './components/GroupCallOverlay';
import PrivateRoute from './components/PrivateRoute';
import MessengerLayout from './components/MessengerLayout';
import ShareIntentHandler from './components/ShareIntentHandler';
import NativeMobileBridge from './components/NativeMobileBridge';
import Login from './pages/Login';
import Register from './pages/Register';

const Messages = lazy(() => import('./pages/Messages'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const InfoPage = lazy(() => import('./pages/InfoPage'));
const ShareTarget = lazy(() => import('./pages/ShareTarget'));

function IndexRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-white dark:bg-black" />;
  return <Navigate to={user ? '/messages' : '/login'} replace />;
}

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('config', 'G-LPC6F6RXSC', {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);
  return null;
}

/**
 * Standalone Bazzef Messenger: Bazzef or Google sign-in, then the same
 * messages, voice calls, and video calls as the main app. Nothing else.
 */
function MessengerApp() {
  useEffect(() => {
    document.title = 'Bazzef Messenger';
    const icon = document.querySelector('link[rel="icon"]');
    if (icon) icon.href = '/messenger-icon.png';
    const apple = document.querySelector('link[rel="apple-touch-icon"]');
    if (apple) apple.href = '/messenger-icon.png';
    const theme = document.querySelector('meta[name="theme-color"]');
    if (theme) theme.setAttribute('content', '#4F46E5');
  }, []);

  return (
    <AuthProvider>
      <SocketProvider>
        <ToastProvider>
          <CallProvider>
            <GroupCallProvider>
              <Router>
                <PageViewTracker />
                <NativeMobileBridge />
                <div className="min-h-screen bg-white dark:bg-black text-gray-900 dark:text-gray-100">
                  <ShareIntentHandler />
                  <Suspense fallback={<div className="min-h-screen bg-white dark:bg-black" />}>
                    <Routes>
                      <Route path="/login" element={<Login />} />
                      <Route path="/register" element={<Register />} />
                      <Route path="/forgot-password" element={<ForgotPassword />} />
                      <Route path="/reset-password" element={<ResetPassword />} />
                      <Route path="/terms" element={<InfoPage doc="terms" />} />
                      <Route path="/privacy" element={<InfoPage doc="privacy" />} />
                      <Route path="/share" element={<PrivateRoute><ShareTarget /></PrivateRoute>} />
                      <Route path="/" element={<MessengerLayout />}>
                        <Route index element={<IndexRedirect />} />
                        <Route path="messages" element={<PrivateRoute><Messages /></PrivateRoute>} />
                        <Route path="messages/group/:groupId" element={<PrivateRoute><Messages /></PrivateRoute>} />
                        <Route path="messages/:targetUserId" element={<PrivateRoute><Messages /></PrivateRoute>} />
                        <Route path="*" element={<IndexRedirect />} />
                      </Route>
                    </Routes>
                  </Suspense>
                </div>
              </Router>
              <CallOverlay />
              <GroupCallOverlay />
            </GroupCallProvider>
          </CallProvider>
        </ToastProvider>
      </SocketProvider>
    </AuthProvider>
  );
}

export default MessengerApp;
